/**
 * JARVIS signaling protocol — the single source of truth shared by the
 * Cloudflare Worker (`cloud-signaling`), the phone PWA (`phone-web`) and the
 * Windows app (`desktop`).
 *
 * Scope: signaling only. Camera media never travels through these messages;
 * it goes peer-to-peer over WebRTC, preferably on the USB-tether network.
 */

export const PROTOCOL_VERSION = 1;

/** Wire format version negotiated at connect time. */
export const PROTOCOL_HEADER = 'x-jarvis-protocol';

export type Role = 'pc' | 'phone';

/* -------------------------------------------------------------------------- */
/* HTTP pairing API                                                           */
/* -------------------------------------------------------------------------- */

/** `POST /pair/new` — PC only. Opens a short-lived pairing window. */
export interface PairNewRequest {
  /** Human label shown to the user while the code is live. */
  pcName: string;
  /** Requested lifetime in seconds. Clamped by the Worker. */
  ttlSeconds?: number;
}

export interface PairNewResponse {
  /** Opaque token the QR code carries. Single use, expires. */
  pairingToken: string;
  /** Six digits, for the case where the user types instead of scanning. */
  pairingCode: string;
  /** Absolute URL the phone should open, pairing token already in the fragment. */
  pairingUrl: string;
  expiresAt: number;
  installId: string;
}

/** `POST /pair/claim` — phone only. Requests admission to an install. */
export interface PairClaimRequest {
  pairingToken: string;
  /** Label the PC shows in its paired-devices list. */
  deviceName: string;
  /** Client-generated public half, so the Worker never mints the only copy. */
  devicePublicId: string;
}

export interface PairClaimResponse {
  /** Poll `/pair/status` with this until the PC decides. */
  claimId: string;
  installId: string;
  expiresAt: number;
}

/** `GET /pair/status?claim=…` — phone polls while the PC decides. */
export interface PairStatusResponse {
  state: 'pending' | 'approved' | 'denied' | 'expired';
  /** Present only once, only on `approved`. */
  credential?: DeviceCredential;
}

/**
 * The persistent credential. The secret is delivered exactly once, over TLS,
 * after the PC approves — never embedded in a QR code, never re-sent.
 */
export interface DeviceCredential {
  installId: string;
  deviceId: string;
  deviceSecret: string;
  deviceName: string;
  issuedAt: number;
}

/** `GET /devices` — PC only. */
export interface DeviceRecord {
  deviceId: string;
  deviceName: string;
  devicePublicId: string;
  issuedAt: number;
  lastSeenAt: number | null;
  revokedAt: number | null;
}

export interface DeviceListResponse {
  installId: string;
  devices: DeviceRecord[];
}

/** `POST /devices/revoke` — PC only. A revoked device cannot reconnect. */
export interface DeviceRevokeRequest {
  deviceId: string;
}

/* -------------------------------------------------------------------------- */
/* WebSocket messages                                                         */
/* -------------------------------------------------------------------------- */

interface Base {
  v: typeof PROTOCOL_VERSION;
  /** Millisecond epoch stamped by the sender. Advisory only. */
  ts?: number;
}

/** Server → client, immediately after a socket is accepted. */
export interface HelloMessage extends Base {
  type: 'hello';
  role: Role;
  installId: string;
  /** Own identity: the deviceId for a phone, `'pc'` for the PC. */
  self: string;
  /** Peers already connected when this socket joined. */
  peers: PeerInfo[];
}

export interface PeerInfo {
  role: Role;
  /** `'pc'` or a deviceId. */
  id: string;
  name: string;
}

/** Server → both sides when the peer set changes. */
export interface PresenceMessage extends Base {
  type: 'presence';
  peers: PeerInfo[];
}

/** Server → PC when a phone asks to pair and is waiting for a decision. */
export interface PairRequestMessage extends Base {
  type: 'pair-request';
  claimId: string;
  deviceName: string;
  devicePublicId: string;
  expiresAt: number;
}

/** PC → server. Answers a `pair-request`. */
export interface PairDecisionMessage extends Base {
  type: 'pair-decision';
  claimId: string;
  approve: boolean;
}

/* --- WebRTC relay. The Worker forwards these verbatim and inspects nothing
       beyond the routing envelope. --------------------------------------- */

export interface OfferMessage extends Base {
  type: 'offer';
  /** Target peer id. Phones may only address `'pc'`. */
  to: string;
  from?: string;
  /** WebRTC session this offer belongs to, so late ICE can be discarded. */
  sessionId: string;
  sdp: string;
}

export interface AnswerMessage extends Base {
  type: 'answer';
  to: string;
  from?: string;
  sessionId: string;
  sdp: string;
}

export interface IceMessage extends Base {
  type: 'ice';
  to: string;
  from?: string;
  sessionId: string;
  /** `null` signals end-of-candidates. */
  candidate: IceCandidatePayload | null;
}

export interface IceCandidatePayload {
  candidate: string;
  sdpMid: string | null;
  sdpMLineIndex: number | null;
  usernameFragment?: string | null;
}

/** Either side may tear a session down without dropping the socket. */
export interface ByeMessage extends Base {
  type: 'bye';
  to: string;
  from?: string;
  sessionId: string;
  reason?: string;
}

/** Phone → PC. Real camera facts only; never a guess. */
export interface CameraStateMessage extends Base {
  type: 'camera-state';
  to: string;
  from?: string;
  state: {
    active: boolean;
    facing: 'user' | 'environment' | null;
    width: number | null;
    height: number | null;
    frameRate: number | null;
    /** Capabilities the browser actually reported for this track. */
    supports: { zoom: boolean; torch: boolean; focus: boolean };
  };
}

/** PC → phone. Remote control of the live camera track. */
export interface CameraCommandMessage extends Base {
  type: 'camera-command';
  to: string;
  from?: string;
  sessionId: string;
  command: 'switch-lens' | 'zoom' | 'torch';
  /** For switch-lens: 'front'|'back', zoom: number, torch: boolean */
  value?: string | number | boolean;
}

/** Phone → PC. Real capabilities the device actually reported — never a guess. */
export interface CameraCapabilitiesMessage extends Base {
  type: 'camera-capabilities';
  to: string;
  from?: string;
  sessionId: string;
  torch: boolean;
  zoom: { min: number; max: number; step: number } | null;
  lenses: string[];
}

/** Keepalive. The DO answers with the same nonce so RTT is measurable. */
export interface PingMessage extends Base {
  type: 'ping';
  nonce: string;
}

export interface PongMessage extends Base {
  type: 'pong';
  nonce: string;
}

export type SignalErrorCode =
  | 'bad-request'
  | 'unauthorized'
  | 'revoked'
  | 'expired'
  | 'peer-offline'
  | 'protocol-version'
  | 'rate-limited'
  | 'internal';

export interface ErrorMessage extends Base {
  type: 'error';
  code: SignalErrorCode;
  message: string;
  /** Echoes the offending message type when one could be parsed. */
  about?: string;
}

/** Anything a client may send to the Worker. */
export type ClientMessage =
  | OfferMessage
  | AnswerMessage
  | IceMessage
  | ByeMessage
  | CameraStateMessage
  | CameraCommandMessage
  | CameraCapabilitiesMessage
  | PairDecisionMessage
  | PingMessage;

/** Anything the Worker may send to a client. */
export type ServerMessage =
  | HelloMessage
  | PresenceMessage
  | PairRequestMessage
  | OfferMessage
  | AnswerMessage
  | IceMessage
  | ByeMessage
  | CameraStateMessage
  | CameraCommandMessage
  | CameraCapabilitiesMessage
  | PongMessage
  | ErrorMessage;

export type AnyMessage = ClientMessage | ServerMessage;

/** Messages the Worker relays untouched, keyed by their addressing field. */
export const RELAYED_TYPES = ['offer', 'answer', 'ice', 'bye', 'camera-state', 'camera-command', 'camera-capabilities'] as const;
export type RelayedType = (typeof RELAYED_TYPES)[number];

export const PC_PEER_ID = 'pc';

/** WebSocket close codes this protocol defines beyond the RFC set. */
export const CLOSE = {
  NORMAL: 1000,
  UNAUTHORIZED: 4001,
  REVOKED: 4003,
  PROTOCOL_VERSION: 4004,
  REPLACED: 4008,
} as const;

/* -------------------------------------------------------------------------- */
/* Peer-to-peer data channel                                                  */
/* -------------------------------------------------------------------------- */

/**
 * These messages never touch Cloudflare. They ride the WebRTC data channel
 * between the phone and the PC, alongside the camera track.
 *
 * Push-to-talk is additive and off by default: it adds a phone microphone track
 * to the existing peer connection while the user holds the button. It does not
 * involve M.Z, and it does not replace the M.Z wake-word path on the PC.
 */
export const DATA_CHANNEL = 'jarvis';

/** Phone → PC. The user is holding the talk button. */
export interface TalkStartMessage {
  type: 'talk-start';
  /** Correlates the turn with the reply that answers it. */
  turnId: string;
}

/** Phone → PC. The user released the talk button. */
export interface TalkEndMessage {
  type: 'talk-end';
  turnId: string;
}

/** PC → phone. Live transcript of the current turn, as STT produces it. */
export interface TranscriptMessage {
  type: 'transcript';
  turnId: string;
  text: string;
  final: boolean;
}

/** PC → phone. The user-visible answer. Internals are never sent here. */
export interface ReplyMessage {
  type: 'reply';
  turnId: string;
  text: string;
  /** True once the PC has finished speaking this reply. */
  done: boolean;
}

/** PC → phone. A named subsystem failed; the phone shows it verbatim. */
export interface AgentErrorMessage {
  type: 'agent-error';
  turnId?: string;
  subsystem: string;
  message: string;
}

export type DataChannelMessage =
  | TalkStartMessage
  | TalkEndMessage
  | TranscriptMessage
  | ReplyMessage
  | AgentErrorMessage;
